import { useState, useEffect } from 'react';
import { db } from "../../firebase";
import { doc, getDoc, writeBatch, arrayRemove, runTransaction } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import getServerTimestamp from '../../functions/endpoints/GetServerTimestamp';
import * as theme from '../../theme';
import Heading from '../../components/Heading';
import TruCard from '../../components/TruCard';
import dayjs from 'dayjs';
import { Button, Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import ConfirmationModal from '../../components/ConfirmationModal';
import ErrorDisplay from '../../components/ErrorDisplay';

export default function EditPreScanForm() {
  const navigate = useNavigate();
  const { preScanId } = useParams();
  const [clientForm, setClientForm] = useState(null);
  const [activeScans, setActiveScans] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientLoading, setSelectedClientLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeScansLoading, setActiveScansLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [updateEmail, setUpdateEmail] = useState(false);
  const [updateDob, setUpdateDob] = useState(false);

  useEffect(() => {
    if (preScanId) {
      fetchData();
    }
  }, [preScanId]);

  async function fetchData() {
    try {
      setLoading(true);
      setActiveScansLoading(true);
      await Promise.all([fetchClientForm(), fetchActiveScans(), fetchPractitioners(), fetchClients()]);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
      setActiveScansLoading(false);
    }
  }

  async function fetchClientForm() {
    try {
      const scanRef = doc(db, 'preScanForms', preScanId);
      const scanSnap = await getDoc(scanRef);
      if (scanSnap.exists() && scanSnap.data()) {
        setClientForm(scanSnap.data());
      } else {
        setError('Client form data not found.');
      }
    } catch (err) {
      console.error('Failed to fetch client form:', err.message);
      throw err;
    }
  }

  async function fetchActiveScans() {
    try {
      const docRef = doc(db, 'app', 'activeScans');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Filter scans with scanStatus 1 or 2
        const filteredScans = (data.scans || []).filter(
          scan => scan.scanStatus === 1 || scan.scanStatus === 2
        );
        setActiveScans(filteredScans);
      } else {
        setActiveScans([]);
      }
    } catch (err) {
      console.error('Failed to fetch active scans:', err.message);
      throw err;
    }
  }

  async function fetchClients() {
    try {
      const clientMapRef = doc(db, 'app', 'clientMap');
      const clientMapSnap = await getDoc(clientMapRef);
      if (clientMapSnap.exists()) {
        const data = clientMapSnap.data();
        setClients(data.clients || []);
      } else {
        setClients([]);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err.message);
      throw err;
    }
  }

  async function fetchPractitioners() {
    try {
      const managementRef = doc(db, 'app', 'management');
      const managementSnap = await getDoc(managementRef);
      if (managementSnap.exists()) {
        const data = managementSnap.data();
        setPractitioners(data.practitioners || []);
      } else {
        setPractitioners([]);
      }
    } catch (err) {
      console.error('Failed to fetch practitioners:', err.message);
      throw err;
    }
  }

  async function handleScanSelect(scan) {
    setSelectedClientLoading(true);
    setError('');

    try {
      const clientRef = doc(db, 'clients', scan.clientId);
      const clientSnap = await getDoc(clientRef);
      if (clientSnap.exists()) {
        setSelectedClient({ id: scan.clientId, ...clientSnap.data(), scanId: scan.id });
      } else {
        setError('Client not found for selected scan.');
        setSelectedClient(null);
      }
    } catch (err) {
      console.error('Failed to fetch client details:', err.message);
      setError('Failed to load client details: ' + err.message);
      setSelectedClient(null);
    } finally {
      setSelectedClientLoading(false);
    }
  }

  function handleDelete() {
    setModalOpen(true);
  }

  async function handleConfirmDelete() {
    setModalOpen(false);
    setDeleteLoading(true);
    setError('');

    try {
      const batch = writeBatch(db);

      const preScanFormRef = doc(db, 'preScanForms', preScanId);
      batch.delete(preScanFormRef);

      const managementRef = doc(db, 'app', 'management');
      const managementSnap = await getDoc(managementRef);
      if (!managementSnap.exists()) {
        throw new Error('Management document not found.');
      }

      const managementData = managementSnap.data();
      const preScanForms = managementData.preScanForms || [];

      const targetForm = preScanForms.find(form => form.id === preScanId);
      if (!targetForm) {
        console.warn(`No preScanForm found with id ${preScanId} in app/management/preScanForms`);
      } else {
        batch.update(managementRef, {
          preScanForms: arrayRemove(targetForm)
        });
      }

      await batch.commit();

      navigateBack();
    } catch (err) {
      console.error('Failed to delete pre-scan form:', err.message);
      setError('Failed to delete pre-scan form: ' + err.message);
    } finally {
      setDeleteLoading(false);
    }
  }

  function handleAcceptMatch() {
    if (!selectedClient) {
      setError('No client selected to match.');
      return;
    }
    setUpdateEmail(false);
    setUpdateDob(false);
    setMatchModalOpen(true);
  }

  async function handleConfirmMatch() {
    setMatchModalOpen(false);
    setMatchLoading(true);
    setError('');

    try {
      if (!clientForm.consentForm) {
        throw new Error('Pre-scan form is missing consent data, which is required for matching.');
      }

      await runTransaction(db, async (transaction) => {

        const timestamp = await getServerTimestamp();

        // Fetch necessary documents
        const clientRef = doc(db, 'clients', selectedClient.id);
        const scanRef = doc(db, 'clients', selectedClient.id, 'scans', selectedClient.scanId);
        const activeScansRef = doc(db, 'app', 'activeScans');
        const practitionerScansRef = doc(db, 'activeScans', selectedClient.practitionerId);
        const managementRef = doc(db, 'app', 'management');
        const preScanFormRef = doc(db, 'preScanForms', preScanId);

        const [clientSnap, scanSnap, activeScansSnap, practitionerScansSnap, managementSnap] = await Promise.all([
          transaction.get(clientRef),
          transaction.get(scanRef),
          transaction.get(activeScansRef),
          transaction.get(practitionerScansRef),
          transaction.get(managementRef),
        ]);

        if (!clientSnap.exists()) {
          throw new Error('Client document not found.');
        }
        if (!scanSnap.exists()) {
          throw new Error('Scan document not found.');
        }
        if (!activeScansSnap.exists()) {
          throw new Error('Active scans document not found.');
        }
        if (!practitionerScansSnap.exists()) {
          throw new Error('Practitioner Active scans document not found.');
        }
        if (!managementSnap.exists()) {
          throw new Error('Management document not found.');
        }

        // Update client email and/or DOB if selected and different
        const clientUpdates = {};
        const clientEmail = clientSnap.data().email?.toLowerCase()?.trim() || '';
        const formEmail = clientForm.email?.toLowerCase()?.trim() || '';
        if (updateEmail && clientEmail !== formEmail) {
          clientUpdates.email = clientForm.email;
        }
        if (updateDob && clientSnap.data().dob !== dayjs(clientForm.dob).format('YYYY-MM-DD')) {
          clientUpdates.dob = dayjs(clientForm.dob).format('YYYY-MM-DD');
        }
        if (Object.keys(clientUpdates).length > 0) {
          clientUpdates.dateModified = timestamp;
          transaction.update(clientRef, clientUpdates);
        }

        // Update scan with clientForm and consentForm details
        const scanUpdates = {
          clientForm: {
            sex: clientForm.sex || '',
            female: clientForm.female || {
              isPregnant: null,
              isTrying: null,
              isNursing: null,
              ageOfBaby: ''
            },
            isGroupDetox: clientForm.isGroupDetox || null,
            groupDetox: clientForm.groupDetox || '',
            concerns: clientForm.concerns || '',
            dateCreated: clientForm.dateCreated || timestamp,
            dateModified: clientForm.dateModified || timestamp
          },
          consentForm: {
            agreed: clientForm.consentForm.agreed || false,
            dateSigned: clientForm.consentForm.dateSigned || timestamp,
            parentGuardian: clientForm.consentForm.parentGuardian || null,
            referringAgree: clientForm.consentForm.referringAgree || false,
            signature: clientForm.consentForm.signature || '',
            version: clientForm.consentForm.version || 'unknown'
          },
          dateModified: timestamp
        };

        transaction.update(scanRef, scanUpdates);

        // Update activeScans
        const activeScansData = activeScansSnap.data();
        const updatedScans = activeScansData.scans.map((s) => {
          if (s.id === selectedClient.scanId) {
            return { ...s, scanStatus: 3, modifiedDate: timestamp }; // Client Form
          }
          return s;
        });
        transaction.set(activeScansRef, { scans: updatedScans }, { merge: true });

        // Update Practitioner activeScans
        const practitionerScansData = practitionerScansSnap.data();
        const practitionerScans = practitionerScansData.scans.map((s) => {
          if (s.id === selectedClient.scanId) {
            return { ...s, scanStatus: 3, modifiedDate: timestamp }; // Client Form
          }
          return s;
        });
        transaction.set(practitionerScansRef, { scans: practitionerScans }, { merge: true });

        // Delete preScanForm
        transaction.delete(preScanFormRef);

        // Remove from app/management/preScanForms
        const managementData = managementSnap.data();
        const preScanForms = managementData.preScanForms || [];
        const targetForm = preScanForms.find(form => form.id === preScanId);
        if (targetForm) {
          transaction.update(managementRef, {
            preScanForms: arrayRemove(targetForm)
          });
        }
      });

      navigateBack();
    } catch (err) {
      console.error('Failed to accept client match:', err.message);
      setError('Failed to accept client match: ' + err.message);
    } finally {
      setMatchLoading(false);
    }
  }

  function navigateBack() {
    navigate(`/dashboard`);
  }

  // Helper to get practitioner name by ID
  const getPractitionerName = (practitionerId) => {
    const practitioner = practitioners.find(p => p.id === practitionerId);
    return practitioner ? `${practitioner.firstName} ${practitioner.lastName}` : 'Unknown';
  };

  // Helper to check if email or DOB differ
  const isEmailDifferent = selectedClient && clientForm.email?.toLowerCase()?.trim() !== selectedClient.email?.toLowerCase()?.trim();
  const isDobDifferent = selectedClient && dayjs(clientForm.dob).format('YYYY-MM-DD') !== selectedClient.dob;

  if (loading) {
    return (
      <div style={theme.Container.basic}>
        <div style={theme.Heading}>Client Pre-Scan Form</div>
        <div>Loading...</div>
      </div>
    );
  }

  if (error || !clientForm) {
    return (
      <div style={theme.Container.basic}>
        <div style={theme.Heading}>Client Pre-Scan Form</div>
        <div style={{ marginTop: 15, position: 'relative' }}>
          <Box sx={{ marginBottom: 2 }}>
            <ErrorDisplay
              message={error}
              type={'error'}
            />
          </Box>
        </div>
        <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigateBack()}
            style={{ marginTop: 20 }}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...theme.Container.flexColumnContainer, ...theme.Container.center800 }}>
      <div style={theme.Container.flexColumn}>
        <Heading
          title={clientForm && clientForm.firstName && clientForm.lastName ? `${clientForm.firstName} ${clientForm.lastName}` : 'Client Information'}
          onClick={() => navigateBack()}
        />
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          {/* Active Scans Section */}
          <TruCard title="Match to Active Scan" style={{ width: '100%', marginBottom: 20 }}>
            {activeScansLoading ? (
              <Typography>Loading active scans...</Typography>
            ) : activeScans.length === 0 ? (
              <Typography>No active scans waiting on Client Forms.</Typography>
            ) : (
              <div style={{ ...theme.Container.flexColumn, gap: 10 }}>
                {activeScans.map((scan) => (
                  <Box
                    key={scan.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px',
                      border: `1px solid ${theme.Colors.GrayLight}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.Colors.GrayLight,
                      },
                    }}
                    onClick={() => handleScanSelect(scan)}
                  >
                    <div>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {clients && clients.find((c) => c.id === scan.clientId)
                          ? `${clients.find((c) => c.id === scan.clientId).firstName} ${
                              clients.find((c) => c.id === scan.clientId).lastName
                            }`
                          : null}
                      </Typography>
                      <Typography variant="body2">
                        Scan Date: {dayjs(scan.date).format('dddd, MMMM DD')}
                      </Typography>
                    </div>
                    <Typography variant="body2">
                      Status: {scan.scanStatus === 1 ? 'Downpayment Paid' : 'Kit Mailed'}
                    </Typography>
                  </Box>
                ))}
              </div>
            )}
          </TruCard>

          {/* Selected Client Details Section */}
          {selectedClient && (
            <div style={{ marginTop: 20 }}>
              <TruCard title="Selected Client Details" style={{ width: '100%', marginBottom: 20 }}>
                {selectedClientLoading ? (
                  <Typography>Loading client details...</Typography>
                ) : (
                  <div style={{ ...theme.Container.flexColumn, gap: 10 }}>
                    <div style={theme.TruCard.SubTextLeft}>
                      <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>First Name:</div>
                      <div style={{ marginBottom: 20 }}>{selectedClient.firstName}</div>
                      <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Last Name:</div>
                      <div style={{ marginBottom: 20 }}>{selectedClient.lastName}</div>
                      <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Email:</div>
                      <div style={{ marginBottom: 20 }}>{selectedClient.email}</div>
                      <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Date of Birth:</div>
                      <div style={{ marginBottom: 20 }}>{dayjs(selectedClient.dob).format('MM-DD-YYYY')}</div>
                      <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Practitioner:</div>
                      <div style={{ marginBottom: 20 }}>{getPractitionerName(selectedClient.practitionerId)}</div>
                    </div>
                    <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAcceptMatch}
                        disabled={matchLoading}
                      >
                        {matchLoading ? 'Processing...' : 'Accept Client Match'}
                      </Button>
                    </Box>
                  </div>
                )}
              </TruCard>
            </div>
          )}

          <Heading
            title="Pre-Scan Form"
            subHeading={true}
          />
          <div style={{ ...theme.Container.flexColumn, flex: 2, gap: 20 }}>
            <TruCard title="Personal Information" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>First Name:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.firstName}</div>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Last Name:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.lastName}</div>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Email:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.email}</div>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Date of Birth:</div>
                <div style={{ marginBottom: 20 }}>{dayjs(clientForm.dob).format('MM-DD-YYYY')}</div>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Sex:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.sex === 'M' ? 'Male' : clientForm.sex === 'F' ? 'Female' : 'N/A'}</div>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Practitioner:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.practitioner}</div>
              </div>
            </TruCard>
            {clientForm.sex === 'F' && (
              <TruCard title="Female-Specific Information" style={{ width: '100%' }}>
                <div style={theme.TruCard.SubTextLeft}>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Pregnant:</div>
                  <div style={{ marginBottom: 20 }}>{clientForm.female.isPregnant ? 'Yes' : 'No'}</div>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Trying to Get Pregnant:</div>
                  <div style={{ marginBottom: 20 }}>{clientForm.female.isTrying ? 'Yes' : 'No'}</div>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Nursing:</div>
                  <div style={{ marginBottom: 20 }}>{clientForm.female.isNursing ? 'Yes' : 'No'}</div>
                  {clientForm.female.isNursing && (
                    <>
                      <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Baby's Age:</div>
                      <div style={{ marginBottom: 20 }}>{clientForm.female.ageOfBaby || 'N/A'}</div>
                    </>
                  )}
                </div>
              </TruCard>
            )}
            <TruCard title="Group Detox Program" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Participating:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.isGroupDetox ? 'Yes' : 'No'}</div>
                {clientForm.isGroupDetox && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Program:</div>
                    <div style={{ marginBottom: 20 }}>{clientForm.groupDetox || 'N/A'}</div>
                  </>
                )}
              </div>
            </TruCard>
            <TruCard title="Concerns/Goals" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Concerns or Goals:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.concerns || 'None provided'}</div>
              </div>
            </TruCard>
          </div>
        </div>
      </div>
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        <Button
          variant="outlined"
          color="error"
          onClick={handleDelete}
          disabled={deleteLoading || matchLoading}
        >
          {deleteLoading ? 'Deleting...' : 'Delete Form'}
        </Button>
      </Box>
      <div style={{ marginTop: 20, padding: 20, textAlign: 'center', fontSize: `0.85em`, color: theme.Colors.GrayMedium }}>
        <div style={{ marginBottom: 6 }}>
          <span style={{ color: theme.Colors.Purple, fontWeight: 600 }}>Completed:</span> {clientForm.dateCreated ? dayjs(clientForm.dateCreated.toDate()).format('MMMM D, YYYY h:mm A') : 'N/A'}
        </div>
        <span style={{ color: theme.Colors.Purple, fontWeight: 600 }}>Modified:</span> {clientForm.dateModified ? dayjs(clientForm.dateModified.toDate()).format('MMMM D, YYYY h:mm A') : 'N/A'}
      </div>
      <ConfirmationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Form Deletion"
        description="Are you sure you want to delete this pre-scan form? This action cannot be undone."
        confirmButtonText="Confirm Deletion"
      />
      <ConfirmationModal
        open={matchModalOpen}
        onClose={() => setMatchModalOpen(false)}
        onConfirm={handleConfirmMatch}
        title="Confirm Client Match"
        description="Are you sure you want to match this pre-scan form to the selected client? Check below to update any mismatched fields (email or date of birth) on the client record."
        confirmButtonText="Confirm Match"
      >
        {isEmailDifferent && (
          <FormControlLabel
            control={
              <Checkbox
                checked={updateEmail}
                onChange={(e) => setUpdateEmail(e.target.checked)}
              />
            }
            label={`Update client Email to ${clientForm.email} from ${selectedClient.email}`}
            sx={{ mb: 2 }}
          />
        )}
        {isDobDifferent && (
          <FormControlLabel
            control={
              <Checkbox
                checked={updateDob}
                onChange={(e) => setUpdateDob(e.target.checked)}
              />
            }
            label={`Update client Date of Birth to ${dayjs(clientForm.dob).format('MM-DD-YYYY')} from ${dayjs(selectedClient.dob).format('MM-DD-YYYY')}`}
            sx={{ mb: 2 }}
          />
        )}
      </ConfirmationModal>
    </div>
  );
}